import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  useCreateQualificationOption,
  useUpdateQualificationOption,
  useDeleteQualificationOption,
  useReorderQualificationOptions,
  QualificationOption,
  QualificationOptionList
} from '@/hooks/useQualificationOptions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { GripVertical, Plus, Trash2, Pencil, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OptionListEditorProps {
  list: QualificationOptionList;
  options: QualificationOption[];
}

export function OptionListEditor({ list, options }: OptionListEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const createMutation = useCreateQualificationOption();
  const updateMutation = useUpdateQualificationOption();
  const deleteMutation = useDeleteQualificationOption();
  const reorderMutation = useReorderQualificationOptions();

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(options);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updates = items.map((item, index) => ({
      id: item.id,
      sort_order: index
    }));

    reorderMutation.mutate(updates);
  };

  const handleAdd = () => {
    if (!newValue.trim() || !newLabel.trim()) return;

    createMutation.mutate(
      {
        list_id: list.id,
        value: newValue.trim(),
        label: newLabel.trim()
      },
      {
        onSuccess: () => {
          setNewValue('');
          setNewLabel('');
          setIsAdding(false);
        }
      }
    );
  };

  const handleStartEdit = (option: QualificationOption) => {
    setEditingId(option.id);
    setEditValue(option.value);
    setEditLabel(option.label);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditLabel('');
  };

  const handleSaveEdit = (id: string) => {
    if (!editValue.trim() || !editLabel.trim()) return;

    updateMutation.mutate(
      {
        id,
        value: editValue.trim(),
        label: editLabel.trim()
      },
      {
        onSuccess: () => {
          handleCancelEdit();
        }
      }
    );
  };

  const handleToggleActive = (option: QualificationOption) => {
    updateMutation.mutate({
      id: option.id,
      is_active: !option.is_active
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const sortedOptions = [...options].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={list.id}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {sortedOptions.map((option, index) => (
                <Draggable key={option.id} draggableId={option.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border bg-card transition-colors',
                        snapshot.isDragging && 'shadow-lg ring-2 ring-primary',
                        !option.is_active && 'opacity-50'
                      )}
                    >
                      <div
                        {...provided.dragHandleProps}
                        className="cursor-grab text-muted-foreground hover:text-foreground"
                      >
                        <GripVertical className="h-5 w-5" />
                      </div>

                      {editingId === option.id ? (
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Valor (interno)"
                            className="h-8 text-sm"
                          />
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            placeholder="Label (exibição)"
                            className="h-8 text-sm"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-primary"
                            onClick={() => handleSaveEdit(option.id)}
                            disabled={updateMutation.isPending}
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{option.label}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              Valor: {option.value}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={option.is_active}
                                onCheckedChange={() => handleToggleActive(option)}
                                disabled={updateMutation.isPending}
                              />
                              <Label className="text-xs text-muted-foreground">
                                {option.is_active ? 'Ativo' : 'Inativo'}
                              </Label>
                            </div>

                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleStartEdit(option)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover opção?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita. A opção "{option.label}" será
                                    permanentemente removida.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(option.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    {deleteMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : null}
                                    Remover
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {isAdding ? (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed bg-muted/30">
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="Valor (interno)"
            className="h-8 text-sm"
            autoFocus
          />
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (exibição)"
            className="h-8 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') {
                setIsAdding(false);
                setNewValue('');
                setNewLabel('');
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={createMutation.isPending || !newValue.trim() || !newLabel.trim()}
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Adicionar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsAdding(false);
              setNewValue('');
              setNewLabel('');
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar nova opção
        </Button>
      )}
    </div>
  );
}
